import { Policy, CloudPolicyType, PolicyTargetType, CloudPolicy, CloudType } from '../types/policies';

// ──────────────────────────────────────────────
// API service for Policy Autopilot
// ──────────────────────────────────────────────

import axios from 'axios';
import { NGROK } from '../../../../APIs';
import { normalizeCloudPolicies, normalizeCloudPolicy } from '../lib/policyTransforms';

export interface LlmTaskStatus {
  tenantName: string;
  cloudAccountId: string;
  llmTask: string;
  status: string;
  startTime?: string;
  endTime?: string;
}

export const fetchLlmTaskStatus = async (
  tenantName: string,
  cloudAccountId: string,
): Promise<LlmTaskStatus[]> => {
  const response = await axios.get(`${NGROK}/api/llm-task/status`, {
    params: {
      tenantName,
      cloudAccountId,
    },
  });
  return response.data || [];
};

export interface GeneratePolicyRequest {
  tenantName: string;
  cloudId: string;
  cloudType: string;
  userPrompt: string;
  cloudPolicyType?: CloudPolicyType;
  policyTargetTypes?: PolicyTargetType[];
  includedUsers: string[];
  includedNHIs: string[];
  includedResources: string[];
  linkedAlerts: string[];
}

export interface SavePolicyRequest {
  policies: CloudPolicy[];
  cloudType?: string;
  tenantName?: string;
  cloudAccountId?: string;
}

export interface UpdatePolicyRequest {
  policyId?: string;
  title?: string;
  description?: string;
  severity?: string;
  includedUsers?: string[];
  includedNHIs?: string[];
  includedResources?: string[];
  excludedUsers?: string[];
  excludedNHIs?: string[];
  excludedResources?: string[];
  policyTargetTypes?: PolicyTargetType[];
  policyEnableData?: any;
  policyDisableData?: any;
  executionHistory?: any;
  policyMetadata?: any;
  linkedAlerts?: string[];
  cloudType?: string;
  tenantName?: string;
  cloudId?: string;
}

export interface PolicyResponse {
  policy: CloudPolicy[];
  alert?: any[]; // Keep any for alert for flexibility, or use CloudAlertDTO
}

export const generatePolicy = async (request: GeneratePolicyRequest): Promise<PolicyResponse> => {
  const response = await axios.post(`${NGROK}/api/cloud/generate-policy`, request, {
    timeout: 450000, // 3 minute timeout
  });
  const data = response.data || { policy: [], alert: [] };
  return {
    ...data,
    policy: normalizeCloudPolicies(data.policy, request.cloudType as CloudType),
    alert: Array.isArray(data.alert) ? data.alert : [],
  };
};

export const savePolicy = async (request: SavePolicyRequest): Promise<CloudPolicy[]> => {
  const response = await axios.post(`${NGROK}/api/cloud/save-policy`, request);
  return normalizeCloudPolicies(response.data || [], request.cloudType as CloudType);
};

export const updatePolicy = async (request: UpdatePolicyRequest): Promise<CloudPolicy> => {
  const response = await axios.post(`${NGROK}/api/cloud/update-policy`, request);
  return normalizeCloudPolicy(response.data, 0, request.cloudType as CloudType);
};

export const fetchPolicies = async (
  tenantName: string,
  cloudId: string,
  cloudType: CloudType
): Promise<Policy[]> => {
  const response = await axios.get(`${NGROK}/api/cloud/get-policy`, {
    params: {
      tenantName,
      cloudId,
      cloudType,
    },
  });

  return normalizeCloudPolicies(response.data || [], cloudType);
};

export interface PolicyExecutionResponse {
  policyId: string;
  success: boolean;
  status: string;
  steps: any[];
}

export const enablePolicy = async (
  policyId: string | number,
  cloudType: string,
  tenantName: string,
  cloudId: string
): Promise<PolicyExecutionResponse> => {
  const response = await axios.post(`${NGROK}/api/cloud/enable-policy`, null, {
    params: { policyId, cloudType, tenantName, cloudId },
  });
  return response.data;
};

export const disablePolicy = async (
  policyId: string | number,
  cloudType: string,
  tenantName: string,
  cloudId: string
): Promise<PolicyExecutionResponse> => {
  const response = await axios.post(`${NGROK}/api/cloud/disable-policy`, null, {
    params: { policyId, cloudType, tenantName, cloudId },
  });
  return response.data;
};

// Delete a policy
export const deletePolicy = async (policyId: string | number): Promise<boolean> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  return true;
};
